import React, { useState } from 'react';

const FormBuilder = () => {
  const [forms, setForms] = useState([
    {
      id: 'form_1',
      name: 'My First Form',
      fields: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
  ]);
  const [currentFormId, setCurrentFormId] = useState('form_1');
  const [selectedField, setSelectedField] = useState(null);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [activeTab, setActiveTab] = useState('builder');
  const [formData, setFormData] = useState({});
  const [showFormMenu, setShowFormMenu] = useState(false);
  const [editingFormName, setEditingFormName] = useState(null);
  const [newFormName, setNewFormName] = useState('');

  // Available field types
  const fieldTypes = [
    { id: 'text', name: 'Text Input', icon: '📝', description: 'Single line text' },
    { id: 'email', name: 'Email', icon: '✉️', description: 'Email address' },
    { id: 'password', name: 'Password', icon: '🔒', description: 'Password field' },
    { id: 'textarea', name: 'Text Area', icon: '📄', description: 'Multi-line text' },
    { id: 'select', name: 'Select', icon: '📋', description: 'Dropdown menu' },
    { id: 'checkbox', name: 'Checkbox', icon: '☑️', description: 'Single checkbox' },
    { id: 'radio', name: 'Radio Button', icon: '🔘', description: 'Multiple choice' },
    { id: 'number', name: 'Number', icon: '🔢', description: 'Numeric input' },
    { id: 'date', name: 'Date', icon: '📅', description: 'Date picker' },
    { id: 'file', name: 'File Upload', icon: '📎', description: 'File attachment' }
  ];

  // Get current form
  const getCurrentForm = () => forms.find(form => form.id === currentFormId);
  const formFields = getCurrentForm()?.fields || [];

  // Generate unique ID
  const generateId = () => Math.random().toString(36).substr(2, 9);

  // Create new form
  const createNewForm = () => {
    const newForm = {
      id: generateId(),
      name: newFormName || `Form ${forms.length + 1}`,
      fields: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setForms(prev => [...prev, newForm]);
    setCurrentFormId(newForm.id);
    setSelectedField(null);
    setNewFormName('');
    setShowFormMenu(false);
  };

  // Update form name
  const updateFormName = (formId, newName) => {
    setForms(prev => prev.map(form => 
      form.id === formId 
        ? { ...form, name: newName, updatedAt: new Date().toISOString() }
        : form
    ));
    setEditingFormName(null);
  };

  // Delete form
  const deleteForm = (formId) => {
    if (forms.length <= 1) {
      alert('Cannot delete the last form. Create another form first.');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this form? All fields will be lost.')) {
      setForms(prev => prev.filter(form => form.id !== formId));
      if (currentFormId === formId) {
        setCurrentFormId(forms.find(form => form.id !== formId)?.id);
      }
      setSelectedField(null);
    }
  };

  // Duplicate form
  const duplicateForm = (formId) => {
    const formToDuplicate = forms.find(form => form.id === formId);
    if (formToDuplicate) {
      const duplicatedForm = {
        ...formToDuplicate,
        id: generateId(),
        name: `${formToDuplicate.name} (Copy)`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        fields: formToDuplicate.fields.map(field => ({
          ...field,
          id: generateId()
        }))
      };
      setForms(prev => [...prev, duplicatedForm]);
      setCurrentFormId(duplicatedForm.id);
      setSelectedField(null);
    }
  };

  // Update current form fields
  const updateCurrentFormFields = (newFields) => {
    setForms(prev => prev.map(form => 
      form.id === currentFormId 
        ? { ...form, fields: newFields, updatedAt: new Date().toISOString() }
        : form
    ));
  };

  // Create new field
  const createField = (type) => ({
    id: generateId(),
    type,
    label: `${fieldTypes.find(f => f.id === type)?.name || type}`,
    placeholder: '',
    required: false,
    options: type === 'select' || type === 'radio' ? ['Option 1', 'Option 2'] : [],
    validation: {}
  });

  // Add field to form
  const addField = (fieldType) => {
    const newField = createField(fieldType);
    const newFields = [...formFields, newField];
    updateCurrentFormFields(newFields);
    setSelectedField(newField);
  };

  // Update field properties
  const updateField = (fieldId, updates) => {
    const newFields = formFields.map(field => 
      field.id === fieldId ? { ...field, ...updates } : field
    );
    updateCurrentFormFields(newFields);
    if (selectedField && selectedField.id === fieldId) {
      setSelectedField({ ...selectedField, ...updates });
    }
  };

  // Delete field
  const deleteField = (fieldId) => {
    const newFields = formFields.filter(field => field.id !== fieldId);
    updateCurrentFormFields(newFields);
    if (selectedField && selectedField.id === fieldId) {
      setSelectedField(null);
    }
  };

  // Move field up/down
  const moveField = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < formFields.length) {
      const newFields = [...formFields];
      [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
      updateCurrentFormFields(newFields);
    }
  };

  // Duplicate field
  const duplicateField = (field, index) => {
    const duplicatedField = {
      ...field,
      id: generateId(),
      label: `${field.label} (Copy)`
    };
    const newFields = [...formFields];
    newFields.splice(index + 1, 0, duplicatedField);
    updateCurrentFormFields(newFields);
    setSelectedField(duplicatedField);
  };

  // Save template
  const saveTemplate = () => {
    if (!templateName.trim()) {
      alert('Please enter a template name');
      return;
    }
    
    const template = {
      id: generateId(),
      name: templateName,
      fields: formFields,
      createdAt: new Date().toISOString()
    };
    
    setSavedTemplates(prev => [...prev, template]);
    setTemplateName('');
    alert('Template saved successfully!');
  };

  // Load template
  const loadTemplate = (template) => {
    const newFields = template.fields.map(field => ({
      ...field,
      id: generateId()
    }));
    updateCurrentFormFields(newFields);
    setSelectedField(null);
    setActiveTab('builder');
  };

  // Delete template
  const deleteTemplate = (templateId) => {
    setSavedTemplates(prev => prev.filter(t => t.id !== templateId));
  };

  // Clear all fields
  const clearAllFields = () => {
    if (window.confirm('Are you sure you want to clear all fields?')) {
      updateCurrentFormFields([]);
      setSelectedField(null);
    }
  };

  // Handle form submission
  const handleFormSubmit = (e) => {
    e.preventDefault();
    console.log('Form Data:', formData);
    alert('Form submitted! Check console for data.');
  };

  // Handle form input change
  const handleInputChange = (fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  // Render form field for preview
  const renderFormField = (field, isPreview = false) => {
    const commonProps = {
      className: "form-control",
      placeholder: field.placeholder,
      required: field.required
    };

    if (isPreview) {
      commonProps.value = formData[field.id] || '';
      commonProps.onChange = (e) => handleInputChange(field.id, e.target.value);
    }

    switch (field.type) {
      case 'textarea':
        return <textarea {...commonProps} rows="3" />;
      
      case 'select':
        return (
          <select {...commonProps}>
            <option value="">Choose...</option>
            {field.options.map((option, idx) => (
              <option key={idx} value={option}>{option}</option>
            ))}
          </select>
        );
      
      case 'checkbox':
        return (
          <div className="form-check">
            <input 
              type="checkbox" 
              className="form-check-input" 
              id={field.id}
              checked={formData[field.id] || false}
              onChange={(e) => isPreview && handleInputChange(field.id, e.target.checked)}
            />
            <label className="form-check-label" htmlFor={field.id}>
              {field.label}
            </label>
          </div>
        );
      
      case 'radio':
        return (
          <div>
            {field.options.map((option, idx) => (
              <div key={idx} className="form-check">
                <input 
                  type="radio" 
                  className="form-check-input" 
                  id={`${field.id}_${idx}`}
                  name={field.id}
                  value={option}
                  checked={formData[field.id] === option}
                  onChange={(e) => isPreview && handleInputChange(field.id, e.target.value)}
                />
                <label className="form-check-label" htmlFor={`${field.id}_${idx}`}>
                  {option}
                </label>
              </div>
            ))}
          </div>
        );
      
      default:
        return <input type={field.type} {...commonProps} />;
    }
  };

  const currentForm = getCurrentForm();

  return (
    <div className="container-fluid vh-100" style={{ backgroundColor: '#f8f9fa' }}>
      {/* Navigation Header */}
      <div className="row">
        <div className="col-12" style={{ backgroundColor: '#a7a7a7' }}>
          <div className="d-flex justify-content-between align-items-center p-3 text-white">
            <div>
              <h3 className="mb-0">🎨 Dynamic Form Builder</h3>
              <small className="text-muted">Build beautiful forms with ease</small>
            </div>
            <div className="d-flex gap-2">
              <button 
                className={`btn ${activeTab === 'builder' ? 'btn-primary' : 'btn-outline-light'}`}
                onClick={() => setActiveTab('builder')}
              >
                🔧 Builder
              </button>
              <button 
                className={`btn ${activeTab === 'preview' ? 'btn-primary' : 'btn-outline-light'}`}
                onClick={() => setActiveTab('preview')}
              >
                👁️ Preview
              </button>
              <button 
                className={`btn ${activeTab === 'templates' ? 'btn-primary' : 'btn-outline-light'}`}
                onClick={() => setActiveTab('templates')}
              >
                💾 Templates
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Form Selector Bar */}
      <div className="row">
        <div className="col-12 bg-white border-bottom p-3">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3">
              <div className="dropdown">
                <button 
                  className="btn btn-outline-primary dropdown-toggle d-flex align-items-center gap-2"
                  onClick={() => setShowFormMenu(!showFormMenu)}
                >
                  📋 <strong>{currentForm?.name}</strong>
                </button>
                {showFormMenu && (
                  <div className="dropdown-menu show" style={{ 
                    position: 'absolute', 
                    top: '100%',
                    left: 0,
                    minWidth: '300px',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    zIndex: 1000
                  }}>
                    <h6 className="dropdown-header">📝 My Forms ({forms.length})</h6>
                    
                    {forms.map((form) => (
                      <div key={form.id} className="d-flex align-items-center p-2 border-bottom">
                        <div className="flex-grow-1">
                          {editingFormName === form.id ? (
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              defaultValue={form.name}
                              onBlur={(e) => updateFormName(form.id, e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  updateFormName(form.id, e.target.value);
                                }
                              }}
                              autoFocus
                            />
                          ) : (
                            <button
                              className={`btn btn-sm w-100 text-start ${currentFormId === form.id ? 'btn-primary' : 'btn-light'}`}
                              onClick={() => {
                                setCurrentFormId(form.id);
                                setSelectedField(null);
                                setShowFormMenu(false);
                              }}
                            >
                              <strong>{form.name}</strong>
                              <br/>
                              <small className="text-muted">{form.fields.length} fields</small>
                            </button>
                          )}
                        </div>
                        <div className="ms-2 d-flex gap-1">
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setEditingFormName(form.id)}
                            title="Rename"
                          >
                            ✏️
                          </button>
                          <button
                            className="btn btn-sm btn-outline-info"
                            onClick={() => duplicateForm(form.id)}
                            title="Duplicate"
                          >
                            📋
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => deleteForm(form.id)}
                            title="Delete"
                            disabled={forms.length <= 1}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                    
                    <div className="p-2 border-top">
                      <div className="input-group input-group-sm">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="New form name"
                          value={newFormName}
                          onChange={(e) => setNewFormName(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              createNewForm();
                            }
                          }}
                        />
                        <button 
                          className="btn btn-success"
                          onClick={createNewForm}
                        >
                          ➕ Create
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Click outside to close dropdown */}
              {showFormMenu && (
                <div 
                  className="position-fixed w-100 h-100"
                  style={{ top: 0, left: 0, zIndex: 999 }}
                  onClick={() => setShowFormMenu(false)}
                ></div>
              )}
              
              <div className="vr"></div>
              <small className="text-muted">
                Last updated: {new Date(currentForm?.updatedAt).toLocaleString()}
              </small>
            </div>
            
            <div className="d-flex gap-2">
              <button 
                className="btn btn-outline-success btn-sm"
                onClick={() => duplicateForm(currentFormId)}
              >
                📋 Duplicate Form
              </button>
              <button 
                className="btn btn-outline-primary btn-sm"
                onClick={() => setNewFormName(`Form ${forms.length + 1}`)}
                onClick={createNewForm}
              >
                ➕ New Form
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="row flex-grow-1" style={{ height: 'calc(100vh - 140px)' }}>
        {activeTab === 'builder' && (
          <>
            {/* Add Fields Sidebar */}
            <div className="col-md-3 bg-white border-end p-3" style={{ overflowY: 'auto' }}>
              <h5 className="mb-3">📦 Add Form Fields</h5>
              <p className="text-muted small mb-3">Click any field type to add it to your form</p>
              
              <div className="d-grid gap-2">
                {fieldTypes.map((fieldType) => (
                  <button
                    key={fieldType.id}
                    onClick={() => addField(fieldType.id)}
                    className="btn btn-outline-primary text-start p-3"
                    style={{ 
                      transition: 'all 0.2s ease',
                      height: 'auto'
                    }}
                  >
                    <div className="d-flex align-items-center">
                      <div className="me-3 d-flex align-items-center justify-content-center" 
                           style={{ 
                             width: '40px', 
                             height: '40px', 
                             backgroundColor: '#f8f9fa',
                             borderRadius: '6px',
                             fontSize: '1.2rem'
                           }}>
                        {fieldType.icon}
                      </div>
                      <div className="flex-grow-1">
                        <div className="fw-bold">{fieldType.name}</div>
                        <small className="text-muted">{fieldType.description}</small>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Template Actions */}
              <div className="mt-4 p-3 bg-light rounded">
                <h6 className="mb-3">💾 Template Actions</h6>
                <div className="d-grid gap-2">
                  <div className="input-group input-group-sm">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Template name"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                    />
                    <button className="btn btn-success" onClick={saveTemplate}>
                      Save
                    </button>
                  </div>
                  <button 
                    className="btn btn-outline-danger btn-sm"
                    onClick={clearAllFields}
                    disabled={formFields.length === 0}
                  >
                    🗑️ Clear All Fields
                  </button>
                </div>
              </div>
            </div>

            {/* Main Form Canvas */}
            <div className="col-md-6 p-4" style={{ overflowY: 'auto' }}>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h5 className="mb-1">🎯 Form Canvas</h5>
                  {formFields.length > 0 && (
                    <small className="text-muted">{formFields.length} field{formFields.length !== 1 ? 's' : ''} added</small>
                  )}
                </div>
              </div>

              <div className="bg-white border rounded p-4 shadow-sm" style={{ minHeight: '500px' }}>
                {formFields.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    <div style={{ fontSize: '5rem', opacity: 0.2 }}>📝</div>
                    <h4 className="mb-3">Start Building Your Form</h4>
                    <p className="mb-4">Select any field type from the sidebar to begin</p>
                    <div className="text-start">
                      <p><strong>Available Field Types:</strong></p>
                      <div className="row">
                        {fieldTypes.slice(0, 6).map((fieldType) => (
                          <div key={fieldType.id} className="col-6 mb-2">
                            <span className="me-2">{fieldType.icon}</span>
                            <small>{fieldType.name}</small>
                          </div>
                        ))}
                      </div>
                      <small className="text-muted">...and more!</small>
                    </div>
                  </div>
                ) : (
                  <div>
                    {formFields.map((field, index) => (
                      <div
                        key={field.id}
                        className={`mb-4 p-3 border rounded-3 ${
                          selectedField?.id === field.id ? 'border-primary bg-light shadow' : 'border-light'
                        }`}
                        onClick={() => setSelectedField(field)}
                        style={{ 
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center mb-2">
                              <span className="badge bg-primary me-2 px-2 py-1">
                                {fieldTypes.find(f => f.id === field.type)?.icon}
                              </span>
                              <label className="form-label fw-bold mb-0 me-2">
                                {field.label}
                              </label>
                              {field.required && <span className="badge bg-danger">Required</span>}
                            </div>
                            <div className="mt-2">
                              {field.type !== 'checkbox' && renderFormField(field)}
                              {field.type === 'checkbox' && renderFormField(field)}
                            </div>
                          </div>
                          
                          <div className="ms-3 d-flex gap-1">
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={(e) => {
                                e.stopPropagation();
                                moveField(index, 'up');
                              }}
                              disabled={index === 0}
                              title="Move up"
                            >
                              ↑
                            </button>
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={(e) => {
                                e.stopPropagation();
                                moveField(index, 'down');
                              }}
                              disabled={index === formFields.length - 1}
                              title="Move down"
                            >
                              ↓
                            </button>
                            <button
                              className="btn btn-sm btn-outline-info"
                              onClick={(e) => {
                                e.stopPropagation();
                                duplicateField(field, index);
                              }}
                              title="Duplicate"
                            >
                              📋
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteField(field.id);
                              }}
                              title="Delete"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                        
                        {selectedField?.id === field.id && (
                          <div className="mt-3 pt-3 border-top">
                            <div className="alert alert-primary py-2 mb-0">
                              <small>
                                <strong>✏️ Selected for editing</strong> - Use the properties panel on the right to modify this field
                              </small>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Properties Panel */}
            <div className="col-md-3 bg-white border-start p-4" style={{ overflowY: 'auto' }}>
              <div className="sticky-top">
                <h5 className="mb-3">⚙️ Field Properties</h5>
                
                {selectedField ? (
                  <div>
                    <div className="alert alert-primary p-3 mb-4">
                      <div className="d-flex align-items-center">
                        <span className="me-2" style={{ fontSize: '1.5rem' }}>
                          {fieldTypes.find(f => f.id === selectedField.type)?.icon}
                        </span>
                        <div>
                          <strong>{fieldTypes.find(f => f.id === selectedField.type)?.name}</strong>
                          <br/>
                          <small>ID: {selectedField.id}</small>
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-bold">Field Label</label>
                      <input
                        type="text"
                        className="form-control"
                        value={selectedField.label}
                        onChange={(e) => updateField(selectedField.id, { label: e.target.value })}
                        placeholder="Enter field label"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-bold">Placeholder Text</label>
                      <input
                        type="text"
                        className="form-control"
                        value={selectedField.placeholder}
                        onChange={(e) => updateField(selectedField.id, { placeholder: e.target.value })}
                        placeholder="Enter placeholder text"
                      />
                    </div>

                    <div className="mb-4">
                      <div className="form-check form-switch">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="required-switch"
                          checked={selectedField.required}
                          onChange={(e) => updateField(selectedField.id, { required: e.target.checked })}
                        />
                        <label className="form-check-label fw-bold" htmlFor="required-switch">
                          Make this field required
                        </label>
                      </div>
                    </div>

                    {(selectedField.type === 'select' || selectedField.type === 'radio') && (
                      <div className="mb-4">
                        <label className="form-label fw-bold">Options</label>
                        {selectedField.options.map((option, index) => (
                          <div key={index} className="input-group mb-2">
                            <span className="input-group-text">{index + 1}</span>
                            <input
                              type="text"
                              className="form-control"
                              value={option}
                              onChange={(e) => {
                                const newOptions = [...selectedField.options];
                                newOptions[index] = e.target.value;
                                updateField(selectedField.id, { options: newOptions });
                              }}
                              placeholder={`Option ${index + 1}`}
                            />
                            <button
                              className="btn btn-outline-danger"
                              onClick={() => {
                                if (selectedField.options.length > 1) {
                                  const newOptions = selectedField.options.filter((_, i) => i !== index);
                                  updateField(selectedField.id, { options: newOptions });
                                }
                              }}
                              disabled={selectedField.options.length <= 1}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        <button
                          className="btn btn-outline-primary w-100"
                          onClick={() => {
                            const newOptions = [...selectedField.options, `Option ${selectedField.options.length + 1}`];
                            updateField(selectedField.id, { options: newOptions });
                          }}
                        >
                          ➕ Add Option
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center text-muted py-5">
                    <div style={{ fontSize: '4rem', opacity: 0.2 }}>⚙️</div>
                    <h6>No field selected</h6>
                    <p>Click on any field in the canvas to edit its properties</p>
                    {formFields.length === 0 && (
                      <div className="mt-4">
                        <p><strong>Quick Start:</strong></p>
                        <p className="small">1. Choose a field type from the left sidebar</p>
                        <p className="small">2. Click on the added field to select it</p>
                        <p className="small">3. Customize it using this properties panel</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {activeTab === 'preview' && (
          <div className="col-12 p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="mb-0">👁️ Form Preview: {currentForm?.name}</h5>
              <div className="text-muted">
                <small>{formFields.length} field{formFields.length !== 1 ? 's' : ''}</small>
              </div>
            </div>
            {formFields.length === 0 ? (
              <div className="alert alert-info">
                <h6>No form fields to preview</h6>
                <p>Go to Form Builder to create your form.</p>
              </div>
            ) : (
              <div className="card shadow">
                <div className="card-header bg-primary text-white">
                  <h5 className="mb-0">📝 {currentForm?.name}</h5>
                </div>
                <div className="card-body">
                  <div>
                    {formFields.map((field) => (
                      <div key={field.id} className="mb-3">
                        {field.type !== 'checkbox' && (
                          <label className="form-label fw-bold">
                            {field.label}
                            {field.required && <span className="text-danger ms-1">*</span>}
                          </label>
                        )}
                        {renderFormField(field, true)}
                      </div>
                    ))}
                    <div className="mt-4">
                      <button 
                        type="button" 
                        className="btn btn-primary btn-lg me-2"
                        onClick={handleFormSubmit}
                      >
                        🚀 Submit Form
                      </button>
                      <button 
                        type="button" 
                        className="btn btn-secondary"
                        onClick={() => setFormData({})}
                      >
                        🔄 Reset
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'templates' && (
          <div className="col-12 p-4">
            <h5 className="mb-4">💾 Saved Templates</h5>
            {savedTemplates.length === 0 ? (
              <div className="alert alert-info">
                <h6>No saved templates</h6>
                <p>Create and save templates from the Form Builder to see them here.</p>
              </div>
            ) : (
              <div className="row">
                {savedTemplates.map((template) => (
                  <div key={template.id} className="col-lg-4 col-md-6 mb-4">
                    <div className="card h-100 shadow-sm">
                      <div className="card-header bg-light">
                        <h6 className="card-title mb-0">📋 {template.name}</h6>
                      </div>
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="badge bg-primary">{template.fields.length} fields</span>
                          <small className="text-muted">
                            {new Date(template.createdAt).toLocaleDateString()}
                          </small>
                        </div>
                        <div className="mb-3">
                          <small className="text-muted">Field types:</small>
                          <div className="mt-1">
                            {template.fields.slice(0, 4).map((field, idx) => (
                              <span key={idx} className="badge bg-light text-dark me-1 mb-1">
                                {fieldTypes.find(f => f.id === field.type)?.icon} {field.type}
                              </span>
                            ))}
                            {template.fields.length > 4 && (
                              <span className="badge bg-light text-dark">
                                +{template.fields.length - 4} more
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="card-footer bg-transparent">
                        <div className="d-grid gap-2">
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => loadTemplate(template)}
                          >
                            📂 Load Template
                          </button>
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => {
                              if (window.confirm('Are you sure you want to delete this template?')) {
                                deleteTemplate(template.id);
                              }
                            }}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FormBuilder;